import { generateUniqueId } from "helpers/generic";
import { AppAction } from "redux/store";

import DockerOrdersReducer from "redux/reducers/dockerOrderReducer";
import DockerWebsiteReducer from "redux/reducers/dockerWebsiteReducer";
import notificationReducer from "redux/reducers/notificationReducer";

import {
 IContainerAPI,
 IContainerData,
 IDockerAPI,
 IDockerNetworkAPI,
 IDockerOrdersAPI,
 IDockerPorts,
 IDockerRepo,
 IDockerServiceAPI,
 IDockerSite,
 IDockerStackAPI,
 IDockerTypeVersions,
 IDockerVolumeAPI
} from "types/api/dockerApiInterface";
import { IDockerSiteAPI } from "types/api/webAppApiInterface";

import { ApiService } from "service/ApiService";

export const getDockerOrders =
 (page: number, limit: number, q?: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<IDockerOrdersAPI>(
    `/docker/order/getorders?page=${page}&limit=${limit}&q=${q || ""}`
   );
   dispatch(DockerOrdersReducer.actions.setDockerOrders(data || {}));
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to load docker orders - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const getDockerImages =
 (id: number, page: number, limit: number, q?: string): AppAction<Promise<IDockerAPI>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<IDockerAPI>(
    `/docker/image/getimages?id_server=${id}&page=${page}&limit=${limit}&q=${q || ""}`
   );
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to load images - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return { dataset: [], totalCount: 0 };
  }
 };

export const getDockerDbTypes =
 (): AppAction<Promise<Array<{ id: number; name: string }>>> => async (dispatch) => {
  try {
   const { data } = await ApiService<Array<{ id: number; name: string }>>(
    "/docker/site/db/gettypes"
   );
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to load db types - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return [];
  }
 };

export const getDockerDbVersions =
 (type: string): AppAction<Promise<Array<{ id: number; name: string }>>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<Array<{ id: number; name: string }>>(
    `/docker/dbversions/getversions?type=${type}`
   );
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to load db versions - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return [];
  }
 };

export const getDockerContainers =
 (id: number, page: number, limit: number, q?: string): AppAction<Promise<IContainerAPI>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<IContainerAPI>(
    `/docker/container/getcontainers?id_server=${id}&page=${page}&limit=${limit}&q=${q || ""}`
   );
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to load containers - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return { dataset: [], totalCount: 0 };
  }
 };

export const getDockerProxy =
 (id: number, page: number, limit: number, q?: string): AppAction<Promise<IContainerAPI>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<IContainerAPI>(
    `/docker/container/getcontainers?id_server=${id}&page=${page}&limit=${limit}&q=${
     q || ""
    }&type=proxy-site`
   );
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to load containers - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return { dataset: [], totalCount: 0 };
  }
 };

export const getDockerNetworks =
 (id: number, page: number, limit: number, q?: string): AppAction<Promise<IDockerNetworkAPI>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<IDockerNetworkAPI>(
    `/docker/network/getnetworks?id_server=${id}&page=${page}&limit=${limit}&q=${q || ""}`
   );
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to load networks - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return { dataset: [], totalCount: 0 };
  }
 };

export const getDockerVolumes =
 (id: number, page: number, limit: number, q?: string): AppAction<Promise<IDockerVolumeAPI>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<IDockerVolumeAPI>(
    `/docker/volume/getvolumes?id_server=${id}&page=${page}&limit=${limit}&q=${q || ""}`
   );
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to load volumes - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return { dataset: [], totalCount: 0 };
  }
 };

export const getDockerStacks =
 (id: number, page: number, limit: number, q?: string): AppAction<Promise<IDockerStackAPI>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<IDockerStackAPI>(
    `/docker/stack/getstacks?id_server=${id}&page=${page}&limit=${limit}&q=${q || ""}`
   );
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to load stacks - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return { dataset: [], totalCount: 0 };
  }
 };

export const getDockerServices =
 (id: number, page: number, limit: number, q?: string): AppAction<Promise<IDockerServiceAPI>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<IDockerServiceAPI>(
    `/docker/service/getservices?id_server=${id}&page=${page}&limit=${limit}&q=${q || ""}`
   );
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to load services - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return { dataset: [], totalCount: 0 };
  }
 };

export const getServerSites =
 (server_id: number, page: number, limit: number, q?: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<IDockerSiteAPI>(
    `/docker/site/getsites?page=${page}&limit=${limit}&q=${q || ""}&server_id_db=${server_id}`
   );
   dispatch(DockerWebsiteReducer.actions.setDockerWebsites(data));
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to load sites - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const getProxySites =
 (proxy_id: string, page: number, limit: number, q?: string): AppAction<Promise<IDockerSiteAPI>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<IDockerSiteAPI>(
    `/docker/site/getsites?page=${page}&limit=${limit}&q=${q || ""}&proxy_id=${proxy_id}`
   );
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to load sites - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return { dataset: [], totalCount: 0 };
  }
 };

export const getTypeVersions =
 (): AppAction<Promise<IDockerTypeVersions | null>> => async (dispatch) => {
  try {
   const { data } = await ApiService<IDockerTypeVersions>("/docker/php/getversions");
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to get php versions - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return null;
  }
 };

export const getRepositorys = (): AppAction<Promise<Array<IDockerRepo>>> => async (dispatch) => {
 try {
  const { data } = await ApiService<Array<IDockerRepo>>("/docker/repo/getrepo");
  return data;
 } catch (error: any) {
  dispatch(
   notificationReducer.actions.addAlert({
    id: generateUniqueId(),
    message: `Failed to get repository - ${error?.message || "unknown error"}`,
    timestamp: Date.now(),
    type: "error"
   })
  );
  return [];
 }
};

export const getPorts = (): AppAction<Promise<Array<IDockerPorts>>> => async (dispatch) => {
 try {
  const { data } = await ApiService<Array<IDockerPorts>>("/docker/port/getports");
  return data;
 } catch (error: any) {
  dispatch(
   notificationReducer.actions.addAlert({
    id: generateUniqueId(),
    message: `Failed to get ports - ${error?.message || "unknown error"}`,
    timestamp: Date.now(),
    type: "error"
   })
  );
  return [];
 }
};

export const getAllSites =
 (id: number): AppAction<Promise<Array<IDockerSite>>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<Array<IDockerSite>>("/docker/site/getbyserverid", "POST", {
    server_id_db: id
   });
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to get sites - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return [];
  }
 };

export const getAllContainers =
 (id: number): AppAction<Promise<Array<IContainerData>>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<Array<IContainerData>>(
    "/docker/container/getbyserverid",
    "POST",
    {
     server_id_db: id
    }
   );
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to get containers - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return [];
  }
 };

export const postCreateContainer =
 (id: number, name: string, repo: string, php_id: string, site: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/container/installcontainer", "POST", {
    server_id_db: id,
    container_name: name,
    repo_name: repo,
    php_id: php_id,
    site_name: site
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Installing container",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to install container - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postDeleteContainer =
 (id: number, container_id: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/container/disinstallcontainer", "POST", {
    server_id_db: id,
    id: container_id
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Deleting container",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to delete container - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postCheckForProxy =
 (id: number): AppAction<Promise<boolean>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/container/checkproxy", "POST", {
    server_id_db: id
   });
   return data;
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to install proxy - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
   return false;
  }
 };

export const postInstallProxy =
 (id: number, name: string, type: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/container/installproxy", "POST", {
    server_id_db: id,
    container_name: name,
    type: type
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Installing proxy",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to install proxy - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postDisableProxy =
 (id: number, name: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/container/disableproxy", "POST", {
    server_id_db: id,
    container_name: name
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Disable proxy",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to disable proxy - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postRebootProxy =
 (id: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/container/restartproxy", "POST", {
    server_id_db: id
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Rebooting proxy....",
      timestamp: Date.now(),
      type: "info"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to reboot proxy - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postRedirectProxy =
 (id: number, name: string, port: number, domain: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>(
    "/docker/container/redirectproxytocontainer",
    "POST",
    {
     server_id_db: id,
     container_name: name,
     port: port,
     domain_name: domain
    }
   );
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Redirecting proxy",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to redirect proxy - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postCreateSite =
 (id: number, name: string, proxy_id: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/createsite", "POST", {
    server_id_db: id,
    site_name: name,
    proxy_id: proxy_id
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Creating site",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to create site - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postApplySsl =
 (id: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/applyssl", "POST", {
    id: id
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Applying ssl",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to apply ssl - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postDeleteSite =
 (id: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/deletesite", "POST", {
    id: id
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Deleting site",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to delete site - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postDeleteAllDataSite =
 (id: number, flag: boolean, revoke_ssl: boolean): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/deletewithcontainer", "POST", {
    id: id,
    flag,
    revoke_ssl
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Deleting site",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to delete site - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postCreateWebsite =
 (id: number, name: string, php_version: string, repository: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/installfullsite", "POST", {
    server_id_db: id,
    domain_name: name,
    site_name: name,
    container_php_name: name,
    php_version: php_version,
    repo_name: repository,
    container_nginx_certbot_name: "nginx_certbot",
    network_name: "nginx-proxy"
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Creating site",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to create site - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postRebootSite =
 (id: number, type: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/container/lifecycle", "POST", {
    id,
    action: "restart",
    type
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Restarting container",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to restart container - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postPowerOffSite =
 (id: number, type: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/container/lifecycle", "POST", {
    id,
    action: "stop",
    type
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Stopping container",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to stop container - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postPowerOnSite =
 (id: number, type: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/container/lifecycle", "POST", {
    id,
    action: "start",
    type
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Starting container",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to start container - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postInstallModule =
 (id: number, module: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/installmodule", "POST", {
    id,
    module
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Installing module on site",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to install module - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postEditSiteTuning =
 (id: number, cpu: number, mem: string, type: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/container/tuning", "POST", {
    id,
    cpu,
    mem,
    type
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Editing site tuning",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to edit site tuning - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postEditDbRemoteAccess =
 (id: number, remote: boolean): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/db/remote", "POST", {
    id,
    remote
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Editing remote access",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to edit remote access - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postExecuteScript =
 (id: number, path: string, script: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/sshscript ", "POST", {
    id,
    path,
    script
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Executing custom script...",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to execute custom script - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postRepairTable =
 (id: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   await ApiService("/docker/site/db/repairtables", "POST", {
    id: id
   });
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: "Repairing tables",
     timestamp: Date.now(),
     type: "success"
    })
   );
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to repair tables - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postLinkCloudbucketToWebsite =
 (id: number, folder: string, bucket_id_db: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/mountbucket", "POST", {
    id,
    folder,
    bucket_id_db
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Linking folder to cloudbucket",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to link folder to cloudbucket - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postRemoveFolderFromCloudbucket =
 (
  id: number,
  bucket_id_db: number,
  folder: string,
  delete_bucket: boolean
 ): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/unmountbucket", "POST", {
    id,
    bucket_id_db,
    folder,
    delete_bucket
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Removing folder from cloudbucket",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to remove folder from cloudbucket - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postResetPrivileges =
 (id: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/resetprivileges", "POST", {
    id
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Resetting privileges",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to reset privileges - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postConnectToDatabase =
 (id: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/odoo/connectdb", "POST", {
    id
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Connecting container to database",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to connect to database - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postResetAdminPassword =
 (id: number, password: string): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/odoo/setpwd", "POST", {
    id,
    password
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Resetting admin password",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to reset admin password - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postRevokeSslCertificate =
 (id: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   const { data } = await ApiService<boolean>("/docker/site/removessl", "POST", {
    id
   });
   if (data) {
    dispatch(
     notificationReducer.actions.addAlert({
      id: generateUniqueId(),
      message: "Revoking ssl certificate",
      timestamp: Date.now(),
      type: "success"
     })
    );
   }
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to revoke ssl certificate - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postCreateExternalConnection =
 (
  id: number,
  db_host: string,
  port: number,
  db_name: string,
  upload_limit: number
 ): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   await ApiService("/docker/externaldb/install", "POST", {
    server_id_db: id,
    upload_limit: `${upload_limit}M`,
    db_host,
    port,
    db_name
   });
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: "Creating external database",
     timestamp: Date.now(),
     type: "success"
    })
   );
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to create external database - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };

export const postRemoveExternalConnection =
 (id: number): AppAction<Promise<void>> =>
 async (dispatch) => {
  try {
   await ApiService("/docker/externaldb/disinstall", "POST", {
    server_id_db: id
   });
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: "Removing external database",
     timestamp: Date.now(),
     type: "success"
    })
   );
  } catch (error: any) {
   dispatch(
    notificationReducer.actions.addAlert({
     id: generateUniqueId(),
     message: `Failed to remove external database - ${error?.message || "unknown error"}`,
     timestamp: Date.now(),
     type: "error"
    })
   );
  }
 };
